import { Injectable, inject } from '@angular/core';

import type { CanvasDataset } from '../../canvas-catalogue.util';
import {
  CanvasOgcFeaturesApiService,
  type CanvasOgcFeature,
} from '../ogc/canvas-ogc-features-api.service';
import type { DatasetQueryParams } from './canvas-dataset-load.utils';

export interface DatasetLoadResult {
  featureCount: number;
  features: CanvasOgcFeature[];
}

@Injectable({ providedIn: 'root' })
export class CanvasDatasetLoadService {
  private readonly ogcFeaturesApi = inject(CanvasOgcFeaturesApiService);
  private loadAbortController: AbortController | null = null;

  /** Cancel any in-flight dataset load. */
  abortLoad(): void {
    this.loadAbortController?.abort();
    this.loadAbortController = null;
  }

  validateDatasetServerUrl(dataset: CanvasDataset): string {
    const serverUrl = dataset.resourceServerUrl?.trim();
    if (!serverUrl) {
      throw new Error('This dataset has no OGC Features resource server URL.');
    }
    return serverUrl;
  }

  async fetchCollectionFeatures(
    dataset: CanvasDataset,
    queryParams: DatasetQueryParams,
  ): Promise<DatasetLoadResult> {
    const serverUrl = this.validateDatasetServerUrl(dataset);
    this.abortLoad();
    this.loadAbortController = new AbortController();
    const signal = this.loadAbortController.signal;

    try {
      const features = await this.ogcFeaturesApi.fetchAllCollectionItems(
        dataset.id,
        serverUrl,
        queryParams,
        { signal },
      );
      return { featureCount: features.length, features };
    } finally {
      if (this.loadAbortController?.signal === signal) {
        this.loadAbortController = null;
      }
    }
  }
}
